import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ChevronUp } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { menuList } from "../../data/menuList/MenuData";
import ichGreenLogo from "../../assets/images/IchName/logoICH.svg"

interface MenuItem {
  id: number;
  name: string;
  url: string;
  submenu?: SubMenuItem[];
}

interface SubMenuItem {
  id: number;
  name: string;
  url: string;
}

const MobileNavBar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const [openSubmenus, setOpenSubmenus] = useState<Record<number, boolean>>({});

  // Initialize submenus based on current path when menu opens
  useEffect(() => {
    if (isOpen) {
      const initialOpenState: Record<number, boolean> = {};
      menuList.forEach(item => {
        if (item.submenu) {
          initialOpenState[item.id] = item.submenu.some(
            sub => location.pathname === sub.url
          );
        }
      });
      setOpenSubmenus(initialOpenState);
    }
  }, [isOpen, location.pathname]);

  const clickScroller = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleSubmenuToggle = (itemId: number) => {
    setOpenSubmenus(prev => ({
      ...Object.fromEntries(
        Object.keys(prev).map(key => [key, false])
      ),
      [itemId]: !prev[itemId]
    }));
  };

  const closeAll = () => {
    setIsOpen(false);
    setOpenSubmenus({});
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      closeAll();
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isItemActive = (item: MenuItem) => {
    return location.pathname === item.url || 
           (item.submenu && item.submenu.some(sub => location.pathname === sub.url));
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md ">
      {/* Main Navbar */}
      <div className="w-full">
        <div className="absolute w-full">
          <div className="flex flex-row">
            <div className="w-2/3 relative border-b-yellow-200 border-b">
              <div className="bg-green-500 py-1"></div>
              <div
                className="absolute -right-3.5 bottom-0 w-4 h-4 
                border-l-[16px] border-l-green-500
                border-b-[16px] border-b-transparent"
              ></div>
            </div>
            <div className="bg-gray-300 py-1 w-1/3"></div>
          </div>
        </div>

        <div className="px-4 py-2 border-b border-gray-300">
          <div className="flex items-center justify-between py-3">
            {/* <div className="text-green-700 text-2xl Blinker-Bold">
              Ceylon Hospital
            </div> */}

            <img src={ichGreenLogo} alt="CeylonHospital"  width={200} height={200}/>

            <button
              onClick={() => setIsOpen(!isOpen)}
              className="focus:outline-none"
              aria-label="Toggle menu"
            >
              {isOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu - Full Screen Overlay */}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-40 mt-[72px]"
              onClick={closeAll}
            />

            <motion.div
              ref={menuRef}
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "tween", ease: "easeInOut" }}
              className="fixed top-18 left-0 h-[calc(100vh-4rem)] w-64 bg-gray-50 shadow-lg z-50 overflow-y-auto"
            >
              <div className="flex flex-col space-y-0 p-4">
                {menuList.map((item) => {
                  const isActive = isItemActive(item);
                  const isSubmenuOpen = openSubmenus[item.id];

                  return (
                    <div key={item.id} className="border-b border-gray-200">
                      {item.submenu ? (
                        <div className="flex flex-col">
                          <button
                            onClick={() => handleSubmenuToggle(item.id)}
                            className={`flex items-center justify-between w-full py-3 px-4 text-left text-lg Blinker-SemiBold transition-colors ${
                              isActive
                                ? "text-white bg-green-500"
                                : "text-gray-800 hover:bg-gray-100"
                            }`}
                          >
                            {item.name}
                            <span className="ml-2 transition-transform">
                              {isSubmenuOpen ? (
                                <ChevronUp size={18} />
                              ) : (
                                <ChevronDown size={18} />
                              )}
                            </span>
                          </button>

                          <AnimatePresence>
                            {isSubmenuOpen && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.2 }}
                                className="pl-6 overflow-hidden"
                              >
                                {item.submenu.map((subItem) => (
                                  <Link
                                    to={subItem.url}
                                    onClick={() => {
                                      closeAll();
                                      clickScroller();
                                    }}
                                    key={subItem.id}
                                    className={`block py-2 px-4 text-base Blinker-Regular  border-b  ${
                                      location.pathname === subItem.url
                                        ? "text-green-600 font-medium"
                                        : "text-gray-600 hover:text-green-500"
                                    }`}
                                  >
                                    {subItem.name}
                                  </Link>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      ) : (
                        <Link
                          to={item.url}
                          onClick={() => {
                            closeAll();
                            clickScroller();
                          }}
                          className={`block py-3 px-4 text-lg Blinker-SemiBold transition-colors ${
                            location.pathname === item.url
                              ? "text-white bg-green-500"
                              : "text-gray-800 hover:bg-gray-100"
                          }`}
                        >
                          {item.name}
                        </Link>
                      )}
                    </div>
                  );
                })}

                <Link to="/book-appointment">
                  <div className="flex justify-end w-full">
                    <button
                      onClick={() => {
                        closeAll();
                        clickScroller();
                      }}
                      className="bg-green-500 text-white py-3 px-12 rounded-md text-xs text-center hover:opacity-85 Blinker-SemiBold mt-8"
                    >
                      Book Appointment
                    </button>
                  </div>
                </Link>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MobileNavBar;



// import React, { useEffect, useRef, useState } from "react";
// import { Link, useLocation } from "react-router-dom";
// import { Menu, X } from "lucide-react";
// import { motion, AnimatePresence } from "framer-motion";
// import { menuList } from "../../data/menuList/MenuData";
// import { ChevronDown, ChevronUp } from "lucide-react";

// const MobileNavBar: React.FC = () => {
//   const [isOpen, setIsOpen] = useState(false);
//   const menuRef = useRef<HTMLDivElement>(null);
//   const location = useLocation();
//   const [openSubmenus, setOpenSubmenus] = useState<Record<number, boolean>>({});

//   const clickScroller = () => {
//     window.scrollTo({
//       top: 0,
//       behavior: "smooth",
//     });
//   };

//   useEffect(() => {
//     const handleClickOutside = (event: MouseEvent) => {
//       if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
//         setIsOpen(false);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   return (
//     <div className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
//       {/* Main Navbar */}

//       <div className="w-full ">
//         <div className="absolute w-full">
//           <div className="flex flex-row">
//             <div className="w-2/3 relative border-b-yellow-200 border-b">
//               <div className="bg-green-500 py-1"></div>
//               <div
//                 className="absolute -right-3.5 bottom-0 w-4 h-4 
//                       border-l-[16px] border-l-green-500
//                       border-b-[16px] border-b-transparent"
//               ></div>
//             </div>
//             <div className="bg-gray-300 py-1 w-1/3"></div>
//           </div>
//         </div>

//         <div className="px-4 py-2 border-b  border-gray-300">
//           {/* <div className="absolute bg-green-500 inset-0 w-56 -z-10 "></div> */}
//           <div className="flex items-center justify-between py-3 ">
//             {/* Logo */}
//             <div className="text-green-500  text-2xl font-bold Blinker-Bold">
//               Ceylon Hospital
//             </div>

//             {/* Mobile Menu Button */}
//             <button
//               onClick={() => setIsOpen(!isOpen)}
//               className="focus:outline-none"
//               aria-label="Toggle menu"
//             >
//               {isOpen ? <X size={28} /> : <Menu size={28} />}
//             </button>
//           </div>
//         </div>
//       </div>

//       {/* Mobile Menu - Full Screen Overlay */}
//       <AnimatePresence>
//         {isOpen && (
//           <>
//             <motion.div
//               initial={{ opacity: 0 }}
//               animate={{ opacity: 1 }}
//               exit={{ opacity: 0 }}
//               className="fixed inset-0 bg-black bg-opacity-50 z-40 mt-[72px] "
//               onClick={() => setIsOpen(false)}
//             />

//             <motion.div
//               ref={menuRef}
//               initial={{ x: "-100%" }}
//               animate={{ x: 0 }}
//               exit={{ x: "-100%" }}
//               transition={{ type: "tween", ease: "easeInOut" }}
//               className="fixed top-18 left-0 h-[calc(100vh-4rem)] w-64 bg-gray-50 shadow-lg z-50 overflow-y-auto"
//             >
//               <div className="flex flex-col space-y-0 p-4">

// {menuList.map((item) => {
//   // Check if current path matches any submenu item
//   const isSubmenuActive = item.submenu?.some(sub => location.pathname === sub.url);
//   // Initialize submenu as open if it contains the current path
//   const isSubmenuOpen = isSubmenuActive || openSubmenus[item.id];
  
//   return (
//     <div key={item.id} className="border-b border-gray-200">
//       {item.submenu ? (
//         <div className="flex flex-col">
//           <button
//             onClick={() => {
//               setOpenSubmenus((prev) => ({
//                 ...Object.keys(prev).reduce((acc, key) => {
//                   acc[Number(key)] = false;
//                   return acc;
//                 }, {} as Record<number, boolean>),
//                 [item.id]: !prev[item.id],
//               }));
//             }}
//             className={`flex items-center justify-between w-full py-3 px-4 text-left text-lg Blinker-SemiBold transition-colors ${
//               location.pathname === item.url || isSubmenuActive
//                 ? "text-white bg-green-500"
//                 : "text-gray-800 hover:bg-gray-100"
//             }`}
//           >
//             {item.name}
//             <span className="ml-2 transition-transform">
//               {isSubmenuOpen ? (
//                 <ChevronUp size={18} />
//               ) : (
//                 <ChevronDown size={18} />
//               )}
//             </span>
//           </button>

//           {/* Animated submenu */}
//           <AnimatePresence>
//             {item.submenu && isSubmenuOpen && (
//               <motion.div
//                 initial={{ opacity: 0, height: 0 }}
//                 animate={{ opacity: 1, height: "auto" }}
//                 exit={{ opacity: 0, height: 0 }}
//                 transition={{ duration: 0.2 }}
//                 className="pl-6 overflow-hidden"
//               >
//                 {item.submenu.map((subItem) => (
//                   <Link
//                     to={subItem.url}
//                     onClick={() => {
//                       setIsOpen(false);
//                       clickScroller();
//                       setOpenSubmenus({});
//                     }}
//                     key={subItem.id}
//                     className={`block py-2 px-4 text-base Blinker-Regular ${
//                       location.pathname === subItem.url
//                         ? "text-green-600 font-medium"
//                         : "text-gray-600 hover:text-green-500"
//                     }`}
//                   >
//                     {subItem.name}
//                   </Link>
//                 ))}
//               </motion.div>
//             )}
//           </AnimatePresence>
//         </div>
//       ) : (
//         <Link
//           to={item.url}
//           onClick={() => {
//             setIsOpen(false);
//             clickScroller();
//             setOpenSubmenus({});
//           }}
//           className={`block py-3 px-4 text-lg Blinker-SemiBold transition-colors ${
//             location.pathname === item.url
//               ? "text-white bg-green-500"
//               : "text-gray-800 hover:bg-gray-100"
//           }`}
//         >
//           {item.name}
//         </Link>
//       )}
//     </div>
//   );
// })}







//                 {/* {menuList.map((item) => (
//                   <div key={item.id} className="border-b border-gray-200">
//                     {item.submenu ? (
//                       <div className="flex flex-col">
//                         <button
//                           onClick={() => {
//                             setOpenSubmenus((prev) => ({
//                               ...Object.keys(prev).reduce((acc, key) => {
//                                 acc[Number(key)] = false;
//                                 return acc;
//                               }, {} as Record<number, boolean>),
//                               [item.id]: !prev[item.id],
//                             }));
//                           }}
//                           className={`flex items-center justify-between w-full py-3 px-4 text-left text-lg Blinker-SemiBold transition-colors ${
//                             location.pathname === item.url ||
//                             item.submenu.some(
//                               (sub) => location.pathname === sub.url
//                             )
//                               ? "text-white bg-green-500"
//                               : "text-gray-800 hover:bg-gray-100"
//                           }`}
//                         >
//                           {item.name}
//                           <span className="ml-2 transition-transform">
//                             {openSubmenus[item.id] ? (
//                               <ChevronUp size={18} />
//                             ) : (
//                               <ChevronDown size={18} />
//                             )}
//                           </span>
//                         </button>

//                         <AnimatePresence>
//                           {item.submenu && openSubmenus[item.id] && (
//                             <motion.div
//                               initial={{ opacity: 0, height: 0 }}
//                               animate={{ opacity: 1, height: "auto" }}
//                               exit={{ opacity: 0, height: 0 }}
//                               transition={{ duration: 0.2 }}
//                               className="pl-6 overflow-hidden"
//                             >
//                               {item.submenu.map((subItem) => (
//                                 <Link
//                                   to={subItem.url}
//                                   onClick={() => {
//                                     setIsOpen(false);
//                                     clickScroller();
//                                     setOpenSubmenus({});
//                                   }}
//                                   key={subItem.id}
//                                   className={`block py-2 px-4 text-base Blinker-Regular ${
//                                     location.pathname === subItem.url
//                                       ? "text-green-600 font-medium"
//                                       : "text-gray-600 hover:text-green-500"
//                                   }`}
//                                 >
//                                   {subItem.name}
//                                 </Link>
//                               ))}
//                             </motion.div>
//                           )}
//                         </AnimatePresence>
//                       </div>
//                     ) : (
//                       <Link
//                         to={item.url}
//                         onClick={() => {
//                           setIsOpen(false);
//                           clickScroller();
//                           setOpenSubmenus({});
//                         }}
//                         className={`block py-3 px-4 text-lg Blinker-SemiBold transition-colors ${
//                           location.pathname === item.url
//                             ? "text-white bg-green-500"
//                             : "text-gray-800 hover:bg-gray-100"
//                         }`}
//                       >
//                         {item.name}
//                       </Link>
//                     )}
//                   </div>
//                 ))} */}

//                 {/* {menuList.map((item) => (
//                   <Link
//                     to={item.url}
//                     onClick={() => {
//                       setIsOpen(false);
//                       clickScroller();
//                     }}
//                     key={item.id}
//                     className={`text-md py-1 px-4 rounded-sm font-normal Blinker-Regular transition border-b ${
//                       location.pathname === item.url
//                         ? "text-white bg-green-500"
//                         : "hover:bg-gray-100"
//                     }`}
//                   >
//                     {item.name}
//                   </Link>
//                 ))} */}

//                 <Link to="/book-appointment">
//                   <div className="flex justify-end w-full">
//                     <button
//                       onClick={() => {
//                         setIsOpen(false);
//                         clickScroller();
//                       }}
//                       className=" bg-green-500 text-white py-3 px-12 rounded-md text-xs text-center hover:opacity-85 Blinker-SemiBold mt-8"
//                     >
//                       Book Appointment
//                     </button>
//                   </div>
//                 </Link>
//               </div>
//             </motion.div>
//           </>
//         )}
//       </AnimatePresence>
//     </div>
//   );
// };

// export default MobileNavBar;
